import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { ElementRef } from "../model/inp/properties.ts";
import type {
  Point,
  SwmmLink,
  SwmmNode,
  SwmmProject,
  SwmmRaingage,
  SwmmSubcatchment,
} from "../model/inp/types.ts";
import type { VertexTarget } from "../model/inp/editNetwork.ts";
import {
  canRemoveVertex,
  linkVertexHandles,
  polygonVertexHandles,
} from "../model/inp/vertexHandles.ts";
import { isGeographicCrs } from "../model/geo/crs.ts";
import { pathMidpointAndAngle } from "../model/inp/linkDirection.ts";
import type { BackdropCalibration } from "../model/inp/backdrop.ts";
import { computeBounds, polygonCentroid, type Bounds } from "../model/inp/bounds.ts";
import { type AreaDrag, areaDragPolygon, representativePoint } from "../model/inp/selection.ts";
import { useT, useLanguage } from "../i18n/hooks.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import {
  elementAriaLabel,
  isHandleFocused,
  isMultiSelected,
  isRovingTarget,
  isSelected,
  linkPathPoints,
  pointActivateOrNudge,
  useNetworkMapState,
  type MapDrag,
} from "./useNetworkMapState.ts";
import { NODE_FILL } from "./mapTheme.ts";
import { MinusIcon } from "./MinusIcon.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { ZoomExtentIcon } from "./ZoomExtentIcon.tsx";

// A plain SVG rendering of the network — not a Leaflet/geographic map:
// SWMM's [COORDINATES] are an arbitrary local drawing plane (matching the
// real SWMM GUI's own "Study Area Map"), not latitude/longitude. Y is
// flipped since SWMM's Y grows upward but SVG's grows downward.
//
// Selection lives in the parent (App.tsx) so the element browser and the
// property panel can select/observe the same element the map does.
//
// Shape editing (vertexHandles.ts): only the selected link or subcatchment
// shows drag handles, since a real model can have hundreds/thousands of
// links/subcatchments. A "vertex" handle sits on an existing point (drag to
// move, double-click to delete); a "midpoint" handle sits between two
// consecutive points (drag to insert a new point there).

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

function viewFromBounds(bounds: Bounds): ViewBox {
  const width = Math.max(bounds.maxX - bounds.minX, 1);
  const height = Math.max(bounds.maxY - bounds.minY, 1);
  const padding = Math.max(width, height) * 0.05;
  return {
    x: bounds.minX - padding,
    y: bounds.minY - padding,
    width: width + 2 * padding,
    height: height + 2 * padding,
  };
}

// Extracted out of NetworkMap's render body and wrapped in React.memo so
// pointer-drag state elsewhere in NetworkMap (lasso/rectangle select, most
// concretely) doesn't force every subcatchment polygon to re-render. The
// memo boundary only holds if `toSvg`/`pointsAttr`/`effectivePoints` stay
// referentially stable across such renders — see their own memoization
// below; a fresh closure for any one of them would silently defeat this.
//
// Only subcatchments are extracted this way for now; links/nodes/raingages
// need more plumbing (vertex handles, drag position) to follow the same
// pattern, but GeoreferencedMap.tsx (near-identical render shape over
// Leaflet) is a real candidate for the same technique later.
//
// Exported (not module-private) so test/ui/components/NetworkMap.test.tsx's
// render-count regression test can render it in isolation — NetworkMap
// itself is still the only real caller.
export const SubcatchmentsLayer = memo(function SubcatchmentsLayer({
  subcatchments,
  polygons,
  nodePosition,
  subcatchmentAnchor,
  visible,
  selected,
  multiSelected,
  rovingRef,
  subcatchmentColors,
  zoomScale,
  toSvg,
  pointsAttr,
  effectivePoints,
  onSelect,
  onSelectKeyDown,
}: {
  subcatchments: readonly SwmmSubcatchment[];
  polygons: ReadonlyMap<string, Point[]>;
  nodePosition: ReadonlyMap<string, Point>;
  subcatchmentAnchor: ReadonlyMap<string, Point>;
  visible: boolean;
  selected: ElementRef | null;
  multiSelected?: ElementRef[];
  rovingRef: ElementRef | null;
  subcatchmentColors?: Map<string, string>;
  zoomScale: number;
  toSvg: (p: Point) => Point;
  pointsAttr: (points: Point[]) => string;
  effectivePoints: (target: VertexTarget, base: Point[]) => Point[];
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void;
  onSelectKeyDown: (e: React.KeyboardEvent, ref: ElementRef) => void;
}) {
  const t = useT();
  return (
    <g className="network-map-subcatchments">
      {visible &&
        subcatchments.map((sub) => {
          const basePolygon = polygons.get(sub.name);
          const polygon = basePolygon
            ? effectivePoints({ section: "POLYGONS", name: sub.name }, basePolygon)
            : null;
          const anchor = polygon ? polygonCentroid(polygon) : undefined;
          const outletPos = nodePosition.get(sub.outlet) ?? subcatchmentAnchor.get(sub.outlet);
          const subSelected = isSelected(selected, "subcatchment", sub.name);
          const subMulti = isMultiSelected(multiSelected, "subcatchment", sub.name);
          const ref: ElementRef = {
            category: "subcatchment",
            kind: "subcatchment",
            name: sub.name,
          };
          return (
            <g key={sub.name}>
              {polygon && (
                <polygon
                  points={pointsAttr(polygon)}
                  className={[
                    "network-map-subcatchment",
                    subSelected && "network-map-subcatchment-selected",
                    subMulti && "network-map-subcatchment-multiselected",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  strokeWidth={(subSelected ? 8 : subMulti ? 5 : 4) * zoomScale}
                  style={
                    subcatchmentColors?.has(sub.name)
                      ? {
                          fill: subcatchmentColors.get(sub.name),
                          fillOpacity: 0.45,
                          stroke: subcatchmentColors.get(sub.name),
                        }
                      : undefined
                  }
                  tabIndex={isRovingTarget(rovingRef, "subcatchment", sub.name) ? 0 : -1}
                  role="button"
                  aria-label={elementAriaLabel(t, "subcatchment", sub.name)}
                  aria-pressed={subSelected || subMulti}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(ref, e.shiftKey);
                  }}
                  onKeyDown={(e) => onSelectKeyDown(e, ref)}
                >
                  <title>{sub.name}</title>
                </polygon>
              )}
              {anchor && outletPos && (
                <line
                  className="network-map-subcatchment-outlet"
                  strokeWidth={3 * zoomScale}
                  x1={toSvg(anchor).x}
                  y1={toSvg(anchor).y}
                  x2={toSvg(outletPos).x}
                  y2={toSvg(outletPos).y}
                />
              )}
            </g>
          );
        })}
    </g>
  );
});

// Links and their flow-direction arrows share the exact same per-link
// inputs, so extracting them as two separate memos would only duplicate the
// props list for no isolation benefit. Kept as two `<g>`s (not merged into
// one pass) so arrows render after links but before nodes — a node marker
// stays on top of any nearby arrow. See SubcatchmentsLayer above for the
// general extraction rationale.
export const LinksLayer = memo(function LinksLayer({
  links,
  vertices,
  nodePosition,
  effectivePoints,
  visibleKinds,
  selected,
  multiSelected,
  rovingRef,
  linkColors,
  zoomScale,
  markerSize,
  showLinkDirection,
  toSvg,
  pointsAttr,
  onSelect,
  onSelectKeyDown,
}: {
  links: readonly SwmmLink[];
  vertices: ReadonlyMap<string, Point[]>;
  nodePosition: ReadonlyMap<string, Point>;
  effectivePoints: (target: VertexTarget, base: Point[]) => Point[];
  /** Per kind (conduit/pump/orifice/weir/outlet), not one flag for the whole category, so hiding "Weir" doesn't hide conduits too. */
  visibleKinds: Readonly<Record<string, boolean>>;
  selected: ElementRef | null;
  multiSelected?: ElementRef[];
  rovingRef: ElementRef | null;
  linkColors?: Map<string, string>;
  zoomScale: number;
  markerSize: number;
  showLinkDirection: boolean;
  toSvg: (p: Point) => Point;
  pointsAttr: (points: Point[]) => string;
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void;
  onSelectKeyDown: (e: React.KeyboardEvent, ref: ElementRef) => void;
}) {
  const t = useT();
  return (
    <>
      <g className="network-map-links">
        {links.map((link) => {
          if (visibleKinds[link.kind] === false) return null;
          const linkPath = linkPathPoints(link, nodePosition, vertices, effectivePoints);
          if (!linkPath) return null;
          const path = [linkPath.from, ...linkPath.vertices, linkPath.to];
          const pointsStr = pointsAttr(path);
          const linkSelected = isSelected(selected, "link", link.name);
          const linkMulti = isMultiSelected(multiSelected, "link", link.name);
          return (
            <g key={link.name} className="network-map-link-hitgroup">
              <polyline
                points={pointsStr}
                className={[
                  "network-map-link",
                  link.kind !== "conduit" && `network-map-link-${link.kind}`,
                  linkSelected && "network-map-link-selected",
                  linkMulti && "network-map-link-multiselected",
                ]
                  .filter(Boolean)
                  .join(" ")}
                strokeWidth={(linkSelected ? 10 : linkMulti ? 8 : 6) * zoomScale}
                style={
                  linkColors?.has(link.name) ? { stroke: linkColors.get(link.name) } : undefined
                }
              />
              <polyline
                points={pointsStr}
                className="network-map-link-hit"
                strokeWidth={markerSize * 0.7}
                tabIndex={isRovingTarget(rovingRef, "link", link.name) ? 0 : -1}
                role="button"
                aria-label={elementAriaLabel(t, link.kind, link.name)}
                aria-pressed={linkSelected || linkMulti}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect({ category: "link", kind: link.kind, name: link.name }, e.shiftKey);
                }}
                onKeyDown={(e) =>
                  onSelectKeyDown(e, { category: "link", kind: link.kind, name: link.name })
                }
              >
                <title>{link.name}</title>
              </polyline>
            </g>
          );
        })}
      </g>

      {/* One arrow per link, at its path's midpoint (following the bent
          conduit's real local direction there, not a straight line between
          its end nodes — see pathMidpointAndAngle). Rendered after links but
          before nodes, so a node marker stays on top of any nearby arrow. */}
      {showLinkDirection && (
        <g className="network-map-link-arrows">
          {links.map((link) => {
            if (visibleKinds[link.kind] === false) return null;
            const linkPath = linkPathPoints(link, nodePosition, vertices, effectivePoints);
            if (!linkPath) return null;
            const arrow = pathMidpointAndAngle(
              [linkPath.from, ...linkPath.vertices, linkPath.to].map(toSvg)
            );
            if (!arrow) return null;
            // Sized larger than the original 0.85 (reported as almost
            // invisible) and outlined for contrast against any fill color,
            // same reasoning as .selection-ring's white halo.
            const size = markerSize * 1.3;
            return (
              <path
                key={link.name}
                d={`M ${size} 0 L ${-size * 0.5} ${size * 0.35} L ${-size * 0.5} ${-size * 0.35} Z`}
                className="network-map-link-arrow"
                strokeWidth={markerSize * 0.25}
                transform={`translate(${arrow.point.x} ${arrow.point.y}) rotate(${arrow.angleDeg})`}
              />
            );
          })}
        </g>
      )}
    </>
  );
});

/**
 * Point start-drag handler, identical across NodesLayer/RaingagesLayer/the
 * main NetworkMap body below (previously each defined its own verbatim
 * copy) — returns a fresh closure per call rather than being memoized
 * itself, so every caller stays free to invoke it directly in its own
 * render body without that closure's identity leaking into any memoized
 * child's props.
 */
function svgStartDrag(allowDrag: boolean, setDrag: Dispatch<SetStateAction<MapDrag | null>>) {
  return function startDrag(next: MapDrag) {
    if (allowDrag) setDrag(next);
  };
}

/**
 * startDrag + keyboard-nudge handler pair for a draggable point (node or
 * raingage) — SVG-only, see pointActivateOrNudge's own header for why
 * GeoreferencedMap.tsx has no keyboard-nudge equivalent.
 */
function svgPointHandlers(
  allowDrag: boolean,
  setDrag: Dispatch<SetStateAction<MapDrag | null>>,
  selected: ElementRef | null,
  markerSize: number,
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void,
  onMoveNode: (ref: ElementRef, point: Point) => void
) {
  return {
    startDrag: svgStartDrag(allowDrag, setDrag),
    handlePointKeyDown(e: React.KeyboardEvent, ref: ElementRef, point: Point) {
      pointActivateOrNudge(e, ref, point, selected, allowDrag, markerSize, onSelect, onMoveNode);
    },
  };
}

// Nodes: circle (junction), triangle (outfall), square (storage), diamond
// (divider) — matching the real SWMM GUI's map. Same extraction pattern as
// SubcatchmentsLayer above. setDrag/allowDrag are passed as plain props
// rather than a pre-bound startDrag callback, so this stays memo-friendly —
// building startDrag fresh on every render is fine since it's never itself
// handed down to another memoized child.
export const NodesLayer = memo(function NodesLayer({
  nodes,
  nodePosition,
  visibleKinds,
  selected,
  multiSelected,
  rovingRef,
  nodeColors,
  zoomScale,
  markerSize,
  allowDrag,
  setDrag,
  toSvg,
  onSelect,
  onMoveNode,
}: {
  nodes: readonly SwmmNode[];
  nodePosition: ReadonlyMap<string, Point>;
  /** Per kind, not per category — see LinksLayer's identical prop. */
  visibleKinds: Readonly<Record<string, boolean>>;
  selected: ElementRef | null;
  multiSelected?: ElementRef[];
  rovingRef: ElementRef | null;
  nodeColors?: Map<string, string>;
  zoomScale: number;
  markerSize: number;
  allowDrag: boolean;
  setDrag: Dispatch<SetStateAction<MapDrag | null>>;
  toSvg: (p: Point) => Point;
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void;
  onMoveNode: (ref: ElementRef, point: Point) => void;
}) {
  const t = useT();
  const { startDrag, handlePointKeyDown } = svgPointHandlers(
    allowDrag,
    setDrag,
    selected,
    markerSize,
    onSelect,
    onMoveNode
  );
  return (
    <g className="network-map-nodes">
      {nodes.map((node) => {
        if (visibleKinds[node.kind] === false) return null;
        const p = nodePosition.get(node.name);
        if (!p) return null;
        const { x, y } = toSvg(p);
        // Every kind shares the same neutral fill by default now (shape
        // alone carries kind, see mapTheme.ts's own header) — only a real
        // per-element override (results-animation heat, thematic map)
        // changes it.
        const color = nodeColors?.get(node.name) ?? NODE_FILL;
        const ref: ElementRef = { category: "node", kind: node.kind, name: node.name };
        const selectedNode = isSelected(selected, "node", node.name);
        const multiNode = isMultiSelected(multiSelected, "node", node.name);
        const commonProps = {
          className: [
            "network-map-node",
            selectedNode && "network-map-node-selected",
            multiNode && "network-map-node-multiselected",
          ]
            .filter(Boolean)
            .join(" "),
          strokeWidth: (selectedNode ? 4 : multiNode ? 3 : 2) * zoomScale,
          fill: color,
          "data-draggable": "true",
          tabIndex: isRovingTarget(rovingRef, "node", node.name) ? 0 : -1,
          role: "button",
          "aria-label": elementAriaLabel(t, node.kind, node.name),
          "aria-pressed": selectedNode || multiNode,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onSelect(ref, e.shiftKey);
          },
          onPointerDown: (e: React.PointerEvent) => {
            e.stopPropagation();
            startDrag({ kind: "node", ref, point: p });
          },
          onKeyDown: (e: React.KeyboardEvent) => handlePointKeyDown(e, ref, p),
        };
        return (
          <g key={node.name}>
            {node.kind === "junction" && (
              <circle cx={x} cy={y} r={markerSize} {...commonProps}>
                <title>{node.name}</title>
              </circle>
            )}
            {node.kind === "outfall" && (
              <polygon
                points={`${x},${y - markerSize * 1.2} ${x - markerSize},${y + markerSize} ${x + markerSize},${y + markerSize}`}
                {...commonProps}
              >
                <title>{node.name}</title>
              </polygon>
            )}
            {node.kind === "storage" && (
              <rect
                x={x - markerSize}
                y={y - markerSize}
                width={markerSize * 2}
                height={markerSize * 2}
                {...commonProps}
              >
                <title>{node.name}</title>
              </rect>
            )}
            {node.kind === "divider" && (
              <polygon
                points={`${x},${y - markerSize} ${x + markerSize},${y} ${x},${y + markerSize} ${x - markerSize},${y}`}
                {...commonProps}
              >
                <title>{node.name}</title>
              </polygon>
            )}
          </g>
        );
      })}
    </g>
  );
});

// Raingages: small drop-like symbol at their [SYMBOLS] position. Same
// extraction pattern as NodesLayer above.
export const RaingagesLayer = memo(function RaingagesLayer({
  raingages,
  symbolPosition,
  visible,
  selected,
  multiSelected,
  rovingRef,
  zoomScale,
  markerSize,
  allowDrag,
  setDrag,
  toSvg,
  onSelect,
  onMoveNode,
}: {
  raingages: readonly SwmmRaingage[];
  symbolPosition: ReadonlyMap<string, Point>;
  visible: boolean;
  selected: ElementRef | null;
  multiSelected?: ElementRef[];
  rovingRef: ElementRef | null;
  zoomScale: number;
  markerSize: number;
  allowDrag: boolean;
  setDrag: Dispatch<SetStateAction<MapDrag | null>>;
  toSvg: (p: Point) => Point;
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void;
  onMoveNode: (ref: ElementRef, point: Point) => void;
}) {
  const t = useT();
  const { startDrag, handlePointKeyDown } = svgPointHandlers(
    allowDrag,
    setDrag,
    selected,
    markerSize,
    onSelect,
    onMoveNode
  );
  return (
    <g className="network-map-raingages">
      {visible &&
        raingages.map((gage) => {
          const p = symbolPosition.get(gage.name);
          if (!p) return null;
          const { x, y } = toSvg(p);
          const ref: ElementRef = { category: "raingage", kind: "raingage", name: gage.name };
          const gageSelected = isSelected(selected, "raingage", gage.name);
          const gageMulti = isMultiSelected(multiSelected, "raingage", gage.name);
          return (
            <circle
              key={gage.name}
              cx={x}
              cy={y}
              r={markerSize * 1.3}
              className={[
                "network-map-raingage",
                gageSelected && "network-map-raingage-selected",
                gageMulti && "network-map-raingage-multiselected",
              ]
                .filter(Boolean)
                .join(" ")}
              strokeWidth={(gageSelected ? 4 : gageMulti ? 3 : 2) * zoomScale}
              data-draggable="true"
              tabIndex={isRovingTarget(rovingRef, "raingage", gage.name) ? 0 : -1}
              role="button"
              aria-label={elementAriaLabel(t, "raingage", gage.name)}
              aria-pressed={gageSelected || gageMulti}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(ref, e.shiftKey);
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                startDrag({ kind: "node", ref, point: p });
              }}
              onKeyDown={(e) => handlePointKeyDown(e, ref, p)}
            >
              <title>{gage.name}</title>
            </circle>
          );
        })}
    </g>
  );
});

export function NetworkMap({
  project,
  selected,
  multiSelected,
  onSelect,
  recenterToken,
  allowDrag,
  onMoveNode,
  onBackgroundClick,
  onMoveVertex,
  onInsertVertex,
  onDeleteVertex,
  focusedVertex,
  visibleKinds,
  drawingPreview,
  selectionShape,
  onAreaSelect,
  crs,
  showLinkDirection,
  showOverview,
  nodeColors,
  linkColors,
  subcatchmentColors,
  backgroundImageBlob,
  backdropCalibration,
}: {
  project: SwmmProject;
  selected: ElementRef | null;
  /** Shift-click multi-selection; empty/absent outside it. */
  multiSelected?: ElementRef[];
  /** shiftKey lets the caller distinguish a plain click (replace selection) from a shift-click (toggle multi-select). */
  onSelect: (ref: ElementRef, shiftKey?: boolean) => void;
  /** Bumped when selecting from the element tree/search, not on a direct map click — so the recenter effect below never re-pans the view for an ordinary click. */
  recenterToken: number;
  /** Off while an add-junction/add-conduit tool is active, so a click there isn't misread as the start of a drag. */
  allowDrag: boolean;
  onMoveNode: (ref: ElementRef, point: Point) => void;
  /** Point in real SWMM-plane coordinates when the empty background is clicked; only acted on in "add junction" mode. */
  onBackgroundClick: (point: Point) => void;
  onMoveVertex: (target: VertexTarget, index: number, point: Point) => void;
  onInsertVertex: (target: VertexTarget, index: number, point: Point) => void;
  onDeleteVertex: (target: VertexTarget, index: number) => void;
  /** Keyboard-only vertex editing cursor; null outside "Edit geometry" or before anything's been cycled to. */
  focusedVertex?: { target: VertexTarget; index: number } | null;
  /** Per element kind, not per category, mirroring ElementBrowser.tsx's eye toggles — a hidden kind's elements (and their vertex handles) are skipped entirely. Missing entries default to visible. */
  visibleKinds: Readonly<Record<string, boolean>>;
  /** In-progress outline of a subcatchment being drawn; empty outside that mode. */
  drawingPreview: Point[];
  /** "select-rect"/"select-lasso" mode; null means a background drag pans instead. */
  selectionShape?: "rect" | "lasso" | null;
  /** Polygon in real SWMM-plane coordinates once a rectangle/lasso drag ends (a rectangle is just its own 4 corners). */
  onAreaSelect?: (polygon: Point[]) => void;
  /** Only used for the coordinate readout's "X"/"Y" vs "Lon"/"Lat" label — this view always plots the project's raw [COORDINATES] regardless of CRS. */
  crs?: string | null;
  /** Whether to draw a flow-direction arrow at each link's path midpoint. */
  showLinkDirection: boolean;
  /** Corner minimap toggle, shared state with GeoreferencedMap.tsx. */
  showOverview: boolean;
  /** Results-animation overlay: node name -> heat color for the current report timestamp, overriding NODE_FILL below. */
  nodeColors?: Map<string, string>;
  /** Same as `nodeColors`, for link stroke color (applied via inline style, since it's normally set by CSS class). */
  linkColors?: Map<string, string>;
  /** Same idea, for subcatchment fill color — only populated by the thematic-map overlay, never by results-animation. */
  subcatchmentColors?: Map<string, string>;
  /** Raw background-image bytes; converted to an object URL locally (see the effect below) so the URL's lifecycle stays next to the one place that renders it. */
  backgroundImageBlob?: Blob | null;
  /** The image's world-coordinate extent; meaningless without `backgroundImageBlob`. */
  backdropCalibration?: BackdropCalibration | null;
}) {
  const t = useT();
  const language = useLanguage();
  const svgRef = useRef<SVGSVGElement>(null);
  // Mutated directly on every pointermove (not React state) — re-rendering
  // the whole node/link/subcatchment tree that often would be wasteful.
  const coordsRef = useRef<HTMLDivElement>(null);
  const {
    drag,
    setDrag,
    dragRef,
    nodePosition,
    symbolPosition,
    subcatchmentAnchor,
    effectivePoints,
    selectedLink,
    selectedSubcatchment,
    rovingRef,
  } = useNetworkMapState(project, selected);

  const bounds = useMemo(() => computeBounds(project), [project]);

  // Defers mounting the 4 heavy per-element layers until after the browser
  // has painted the base <svg> + the loading banner below — mounting all
  // elements synchronously freezes the main thread for 1-2.5s on a large
  // network (tens of thousands of nodes/links), with no indication anything
  // is loading. A double requestAnimationFrame (not a single one, and not
  // setTimeout) is needed: confirmed live that a single rAF can still fire
  // before the browser's own paint, while setTimeout has no such guarantee.
  const [layersReady, setLayersReady] = useState(false);
  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setLayersReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  // Current viewBox, replaced by wheel-zoom/pan/recenter below. Deliberately
  // not reset on every `bounds` change (any edit moves the bounding box a
  // little) — only App.tsx's `key={loaded.id}`, remounting this component
  // when the open project itself changes, resets a manually-chosen zoom/pan.
  // The catch-up effect below handles a brand-new empty project, where
  // `bounds` starts null and the lazy initializer never gets a fit to use.
  const [view, setView] = useState<ViewBox | null>(() => (bounds ? viewFromBounds(bounds) : null));
  useEffect(() => {
    if (view === null && bounds) setView(viewFromBounds(bounds));
  }, [bounds, view]);

  // Re-derives the object URL only when `backgroundImageBlob` itself changes
  // (not every render) — otherwise every pan/zoom re-render would leak a new
  // object URL pinning the decoded image in memory.
  const [backdropUrl, setBackdropUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!backgroundImageBlob) {
      setBackdropUrl(null);
      return;
    }
    const url = URL.createObjectURL(backgroundImageBlob);
    setBackdropUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [backgroundImageBlob]);

  // SVG Y grows downward, SWMM's plane grows upward — mirror around the
  // vertical midpoint of the data itself; the viewBox (in the same unflipped
  // Y range) still frames it since the mirrored points stay within
  // [minY, maxY]. A plain reflection is its own inverse, so the same
  // function converts screen clicks back to real SWMM-space Y.
  const flipY = (y: number) => (bounds ? bounds.minY + bounds.maxY - y : y);
  // Subtracted from every coordinate that reaches SVG markup (not from
  // `view`/`bounds` themselves, which stay in the project's real, often
  // large, coordinates throughout the rest of this component). A real
  // network's absolute coordinates combined with a deeply zoomed-in viewBox
  // pushed nearby distinct vertices past floating-point precision — reported
  // live as short conduits vanishing (rounded to zero length) when zoomed
  // in. `toDataPoint` below and `handleWheel`/`recenterFromOverview` further
  // down each add this origin back once, right after reading a real screen
  // coordinate, so their output stays in the same absolute space every other
  // caller expects.
  const originX = bounds ? bounds.minX : 0;
  const originY = bounds ? bounds.minY : 0;
  // Memoized on `bounds` alone so a React.memo'd layer taking this as a prop
  // (SubcatchmentsLayer below) keeps a stable identity across unrelated
  // re-renders — a fresh closure every render would defeat that memoization.
  const toSvg = useMemo(
    () =>
      (p: Point): Point => ({ x: p.x - originX, y: flipY(p.y) - originY }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bounds]
  );
  // Memoized on `toSvg` for the same memo-stability reason. Kept above the
  // `!bounds || !view` early return below — a Hook can never sit after a
  // conditional return.
  const pointsAttr = useMemo(
    () => (points: Point[]) => points.map((p) => `${toSvg(p).x},${toSvg(p).y}`).join(" "),
    [toSvg]
  );
  // Overview minimap content: every link as one combined path (not hundreds
  // of separate lines) for rendering cost; nodes/subcatchments are skipped
  // since a bare pipe layout is already enough to orient by. Memoized: this
  // only actually depends on the network's own layout (`toSvg` is itself
  // memoized on `bounds` alone, never on `view`), so it never needs
  // recomputing on pan/zoom — before this fix, it rebuilt from scratch on
  // EVERY render, including every single pan frame. Kept above the
  // `!bounds || !view` early return below, same reason as `toSvg`/`pointsAttr`.
  const overviewLinksPath = useMemo(() => {
    if (!showOverview) return "";
    let path = "";
    for (const link of project.links) {
      const from = nodePosition.get(link.fromNode);
      const to = nodePosition.get(link.toNode);
      if (!from || !to) continue;
      const a = toSvg(from);
      const b = toSvg(to);
      path += `M ${a.x} ${a.y} L ${b.x} ${b.y} `;
    }
    return path;
  }, [showOverview, project.links, nodePosition, toSvg]);
  // Shared by both SubcatchmentsLayer and LinksLayer below — neither shape
  // has an arrow-key move of its own (a link only moves via its individual
  // vertex handles further down), so both reduce to the same plain
  // Enter/Space select.
  const onShapeSelectKeyDown = useCallback(
    (e: React.KeyboardEvent, ref: ElementRef) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      onSelect(ref, e.shiftKey);
    },
    [onSelect]
  );

  /** Raw screen -> SVG-space point; `toDataPoint` below adds the real-world origin back on top. */
  function screenToSvgPoint(clientX: number, clientY: number): Point | null {
    const svg = svgRef.current;
    const screenCTM = svg?.getScreenCTM();
    if (!svg || !screenCTM) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const transformed = point.matrixTransform(screenCTM.inverse());
    return { x: transformed.x, y: transformed.y };
  }

  function toDataPoint(clientX: number, clientY: number): Point | null {
    const p = screenToSvgPoint(clientX, clientY);
    return p ? { x: p.x + originX, y: flipY(p.y + originY) } : null;
  }

  // A single window-level listener while a drag is active, not per-node
  // handlers — the pointer routinely moves faster than the small marker
  // it's dragging. Keyed on `drag !== null`, not the full `drag` value —
  // `setDrag` inside `handleMove` fires on every pointermove, and keying
  // this effect on `drag` re-subscribed the listeners on every one of those,
  // leaving a real gap where a pointerup landing between one render's
  // cleanup and the next render's re-subscribe was silently dropped (the
  // same class of bug the area-drag effect below already documents and
  // fixes the same way — its own comment names this effect as the still-
  // buggy pattern). `handleUp` reads `dragRef.current` (kept live by
  // `useNetworkMapState`) instead of the closed-over `drag`, which could be
  // stale by the time a listener registered at drag-start finally fires.
  useEffect(() => {
    if (!drag) return;
    function handleMove(e: PointerEvent) {
      const point = toDataPoint(e.clientX, e.clientY);
      if (point) setDrag((current) => (current ? { ...current, point } : current));
    }
    function handleUp(e: PointerEvent) {
      const point = toDataPoint(e.clientX, e.clientY);
      const current = dragRef.current;
      if (point && current) {
        if (current.kind === "node") onMoveNode(current.ref, point);
        else if (current.kind === "vertex") onMoveVertex(current.target, current.index, point);
        else onInsertVertex(current.target, current.index, point);
      }
      setDrag(null);
    }
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag !== null]);

  // Background drag-to-pan — independent of `allowDrag` (panning isn't an
  // edit). Blocked only when the pointerdown lands on an element marked
  // `data-draggable` (node/raingage/vertex handles have their own
  // onPointerDown above) — `closest()` rather than an exact target check,
  // since subcatchment polygons cover most of the canvas and would
  // otherwise block panning almost everywhere.
  //
  // suppressNextClickRef: set by the pan/area-drag pointermove handlers
  // below the moment real movement happens, and read by the SVG's onClick
  // further down — a browser still fires a trailing `click` right after a
  // completed mousedown->move->up on the same element, which would
  // otherwise be misread as "clicked empty background" and wipe out the
  // just-finished selection (confirmed live).
  const suppressNextClickRef = useRef(false);

  const panRef = useRef<{
    startClientX: number;
    startClientY: number;
    startView: ViewBox;
    scale: number;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  // Rectangle/lasso drag-select: a background drag draws a selection shape
  // instead of panning when `selectionShape` is set. `areaDragRef` holds the
  // authoritative live value (written synchronously, read by `handleUp`);
  // `areaDrag` state only drives the preview render — keying the
  // pointermove/pointerup effect on the full value instead (like the
  // node/vertex-drag effect above does) left a real gap where a pointerup
  // landing between one render's cleanup and the next render's re-subscribe
  // was silently dropped (found live via a fast synthetic Playwright drag).
  const areaDragRef = useRef<AreaDrag | null>(null);
  const [areaDrag, setAreaDrag] = useState<AreaDrag | null>(null);
  const isAreaDragging = areaDrag !== null;
  useEffect(() => {
    if (selectionShape) return;
    // An ABORTED drag (the tool switched away mid-drag, e.g. Escape/picking
    // a different toolbar tool) never reaches `handleUp` below — the one
    // place that otherwise consumes `suppressNextClickRef` once the drag's
    // own trailing click fires. Left armed, that flag would silently
    // swallow the very next real, unrelated click anywhere on the map.
    if (areaDragRef.current) suppressNextClickRef.current = false;
    areaDragRef.current = null;
    setAreaDrag(null);
  }, [selectionShape]);

  useEffect(() => {
    if (!isAreaDragging) return;
    function handleMove(e: PointerEvent) {
      const current = areaDragRef.current;
      const point = toDataPoint(e.clientX, e.clientY);
      if (!current || !point) return;
      suppressNextClickRef.current = true;
      const next: AreaDrag =
        current.shape === "rect"
          ? { ...current, current: point }
          : { ...current, points: [...current.points, point] };
      areaDragRef.current = next;
      setAreaDrag(next);
    }
    function handleUp() {
      const current = areaDragRef.current;
      if (current) onAreaSelect?.(areaDragPolygon(current));
      areaDragRef.current = null;
      setAreaDrag(null);
    }
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAreaDragging]);

  function handleBackgroundPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (!view) return;
    if (e.target instanceof Element && e.target.closest("[data-draggable]")) return;
    if (selectionShape) {
      const point = toDataPoint(e.clientX, e.clientY);
      if (!point) return;
      const next: AreaDrag =
        selectionShape === "rect"
          ? { shape: "rect", start: point, current: point }
          : { shape: "lasso", points: [point] };
      areaDragRef.current = next;
      setAreaDrag(next);
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // preserveAspectRatio="xMidYMid meet" fits the whole viewBox inside the
    // container, letterboxing one axis — the smaller of the two candidate
    // scales is the one actually in effect on screen.
    const scale = Math.min(rect.width / view.width, rect.height / view.height);
    panRef.current = { startClientX: e.clientX, startClientY: e.clientY, startView: view, scale };
    setIsPanning(true);
  }

  useEffect(() => {
    if (!isPanning) return;
    function handleMove(e: PointerEvent) {
      const pan = panRef.current;
      if (!pan) return;
      suppressNextClickRef.current = true;
      const dxView = (e.clientX - pan.startClientX) / pan.scale;
      const dyView = (e.clientY - pan.startClientY) / pan.scale;
      setView({ ...pan.startView, x: pan.startView.x - dxView, y: pan.startView.y - dyView });
    }
    function handleUp() {
      panRef.current = null;
      setIsPanning(false);
    }
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [isPanning]);

  // Wheel-to-zoom, centered on the cursor — a real (non-passive) native
  // listener, not React's onWheel: preventDefault() inside a React wheel
  // handler can be silently ignored since React may mark that listener
  // passive. view/bounds are read from refs so this effect only re-attaches
  // when the SVG node itself changes, not on every scroll tick.
  const viewForWheelRef = useRef(view);
  const boundsForWheelRef = useRef(bounds);
  useEffect(() => {
    viewForWheelRef.current = view;
    boundsForWheelRef.current = bounds;
  });
  // Whether the SVG is actually rendered — same condition the early-return
  // check just above this effect body uses.
  const svgIsRendered = bounds !== null && view !== null;
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    function handleWheel(e: WheelEvent) {
      const currentView = viewForWheelRef.current;
      const currentBounds = boundsForWheelRef.current;
      if (!currentView || !currentBounds) return;
      e.preventDefault();
      const svgPoint = screenToSvgPoint(e.clientX, e.clientY);
      const raw = svgPoint && {
        x: svgPoint.x + currentBounds.minX,
        y: svgPoint.y + currentBounds.minY,
      };
      if (!raw) return;
      const base = viewFromBounds(currentBounds);
      const minWidth = base.width / 20;
      const maxWidth = base.width * 3;
      const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, currentView.width * factor));
      const ratio = newWidth / currentView.width;
      setView({
        x: raw.x - (raw.x - currentView.x) * ratio,
        y: raw.y - (raw.y - currentView.y) * ratio,
        width: newWidth,
        height: currentView.height * ratio,
      });
    }
    svg.addEventListener("wheel", handleWheel, { passive: false });
    return () => svg.removeEventListener("wheel", handleWheel);
    // Re-runs whenever the SVG transitions between rendered/not-rendered
    // (same condition as the early-return check below), not on every
    // pan/zoom tick. This used to be `deps=[]` (attach once, at mount) — but
    // a project that starts with no coordinates mounts this effect while
    // `svgRef.current` is still null (the early return renders no <svg>
    // yet), so the listener never attached, and with an empty deps array it
    // never got another chance even once the project gained coordinates
    // later — wheel-zoom stayed permanently dead for the whole session.
  }, [svgIsRendered]);

  // Recenters only when selecting from the element tree/search
  // (`recenterToken`), never on a direct map click — panning on every click
  // read as the view jumping unprompted even when the element was already
  // visible. Pan-only, same as GeoreferencedMap.tsx's PanToSelection, so a
  // manually chosen zoom level isn't fought.
  useEffect(() => {
    const point = selected ? representativePoint(project, selected) : null;
    if (!point) return;
    const target = toSvg(point);
    setView((current) =>
      current
        ? { ...current, x: target.x - current.width / 2, y: target.y - current.height / 2 }
        : current
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterToken]);

  if (!bounds || !view) {
    return <p className="network-map-empty">{t("networkMap.noCoordinates")}</p>;
  }

  const width = Math.max(bounds.maxX - bounds.minX, 1);
  const height = Math.max(bounds.maxY - bounds.minY, 1);

  // Scaled against the current viewBox, not the network's fixed extent, so
  // a marker covers the same number of screen pixels regardless of zoom
  // (same approach as GeoreferencedMap.tsx's fixed-pixel CircleMarker/
  // divIcon markers). The previous bounds-based version kept markers a
  // fixed real-world size, which grew without limit when zoomed in —
  // reported live as nodes turning into oversized, overlapping blobs on a
  // dense real network.
  const markerSize = Math.max(view.width, view.height) * 0.01;
  // Every other stroke-width in this file's CSS (links, outlines, borders,
  // handles, selection ring) is a fixed CSS number calibrated against the
  // old bounds-based markerSize (i.e. against zoomScale === 1); applying it
  // as an inline `strokeWidth={cssValue * zoomScale}` (which wins over the
  // CSS class) keeps their proportions consistent at any zoom level. Hover's
  // width bump is left as a plain CSS :hover with the bump dropped (color
  // only) since it's a transient affordance, not something that needs to
  // hold proportion.
  const zoomScale = markerSize / (Math.max(width, height) * 0.01);
  // Degrees need more decimal places than a projected CRS's linear unit to
  // mean anything at building scale.
  const geographic = crs ? isGeographicCrs(crs) : false;

  /** Still used by the vertex/midpoint handles further down — NodesLayer/RaingagesLayer above call svgStartDrag() themselves instead so a fresh identity here doesn't reach (and defeat) their own memoization. */
  const startDrag = svgStartDrag(allowDrag, setDrag);

  /** Recenters the main view on a click/drag inside the overview minimap, keeping the current zoom level. */
  function recenterFromOverview(e: React.PointerEvent<SVGSVGElement>) {
    const svg = e.currentTarget;
    const screenCTM = svg.getScreenCTM();
    if (!screenCTM || !bounds || !view) return;
    const point = new DOMPoint(e.clientX, e.clientY).matrixTransform(screenCTM.inverse());
    setView({
      ...view,
      x: point.x + originX - view.width / 2,
      y: point.y + originY - view.height / 2,
    });
  }

  return (
    <div className="network-map">
      <svg
        ref={svgRef}
        className={isPanning ? "network-map-svg network-map-svg-panning" : "network-map-svg"}
        viewBox={`${view.x - originX} ${view.y - originY} ${view.width} ${view.height}`}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={handleBackgroundPointerDown}
        onClickCapture={(e) => {
          // Same drag-suppression as onClick below, but in the capture
          // phase and stopping propagation outright — a drag release
          // landing on top of an element would otherwise still reach that
          // element's own onClick (which already calls stopPropagation, so
          // the bubble-phase handler below never sees it), silently
          // replacing the just-finished selection gesture with a plain
          // click-select (confirmed live with a lasso ending on a node).
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            e.stopPropagation();
          }
        }}
        onPointerMove={(e) => {
          if (!coordsRef.current) return;
          const point = toDataPoint(e.clientX, e.clientY);
          // t() is just a function call, cheap enough to run on every pointermove.
          coordsRef.current.textContent = !point
            ? ""
            : geographic
              ? t("networkMap.coordsGeographic", {
                  lon: formatNumber(point.x, language, 6),
                  lat: formatNumber(point.y, language, 6),
                })
              : t("networkMap.coordsPlane", {
                  x: formatNumber(point.x, language, 2),
                  y: formatNumber(point.y, language, 2),
                });
        }}
        onPointerLeave={() => {
          if (coordsRef.current) coordsRef.current.textContent = "";
        }}
        onClick={(e) => {
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
          }
          const point = toDataPoint(e.clientX, e.clientY);
          if (point) onBackgroundClick(point);
        }}
      >
        {/* Background image, drawn first so every other layer paints over
            it. Math.min/max of the transformed corners (not raw
            lowerLeft/upperRight order) — flipY can swap which corner ends
            up smaller, so assuming lowerLeft is always top-left would draw
            a negative-size, invisible rect. */}
        {backdropUrl &&
          backdropCalibration &&
          (() => {
            const a = toSvg(backdropCalibration.lowerLeft);
            const b = toSvg(backdropCalibration.upperRight);
            return (
              <image
                href={backdropUrl}
                x={Math.min(a.x, b.x)}
                y={Math.min(a.y, b.y)}
                width={Math.abs(b.x - a.x)}
                height={Math.abs(b.y - a.y)}
                preserveAspectRatio="none"
              />
            );
          })()}

        {/* Layers mount only once `layersReady` flips true (see the effect
            above) — gating visibility per-layer instead would still
            evaluate every element on first render. */}
        {layersReady && (
          <>
            <SubcatchmentsLayer
              subcatchments={project.subcatchments}
              polygons={project.polygons}
              nodePosition={nodePosition}
              subcatchmentAnchor={subcatchmentAnchor}
              visible={visibleKinds.subcatchment !== false}
              selected={selected}
              multiSelected={multiSelected}
              rovingRef={rovingRef}
              subcatchmentColors={subcatchmentColors}
              zoomScale={zoomScale}
              toSvg={toSvg}
              pointsAttr={pointsAttr}
              effectivePoints={effectivePoints}
              onSelect={onSelect}
              onSelectKeyDown={onShapeSelectKeyDown}
            />

            <LinksLayer
              links={project.links}
              vertices={project.vertices}
              nodePosition={nodePosition}
              effectivePoints={effectivePoints}
              visibleKinds={visibleKinds}
              selected={selected}
              multiSelected={multiSelected}
              rovingRef={rovingRef}
              linkColors={linkColors}
              zoomScale={zoomScale}
              markerSize={markerSize}
              showLinkDirection={showLinkDirection}
              toSvg={toSvg}
              pointsAttr={pointsAttr}
              onSelect={onSelect}
              onSelectKeyDown={onShapeSelectKeyDown}
            />

            <NodesLayer
              nodes={project.nodes}
              nodePosition={nodePosition}
              visibleKinds={visibleKinds}
              selected={selected}
              multiSelected={multiSelected}
              rovingRef={rovingRef}
              nodeColors={nodeColors}
              zoomScale={zoomScale}
              markerSize={markerSize}
              allowDrag={allowDrag}
              setDrag={setDrag}
              toSvg={toSvg}
              onSelect={onSelect}
              onMoveNode={onMoveNode}
            />

            <RaingagesLayer
              raingages={project.raingages}
              symbolPosition={symbolPosition}
              visible={visibleKinds.raingage !== false}
              selected={selected}
              multiSelected={multiSelected}
              rovingRef={rovingRef}
              zoomScale={zoomScale}
              markerSize={markerSize}
              allowDrag={allowDrag}
              setDrag={setDrag}
              toSvg={toSvg}
              onSelect={onSelect}
              onMoveNode={onMoveNode}
            />
          </>
        )}

        {/* Vertex/midpoint handles for the selected link/subcatchment, only
            while allowDrag ("Edit geometry") is on — rendering them anyway
            showed a drag cursor over a selected element even in select mode
            (found live). */}
        {allowDrag && (
          <g className="network-map-handles">
            {selectedLink &&
              visibleKinds[selectedLink.kind] !== false &&
              (() => {
                const linkPath = linkPathPoints(
                  selectedLink,
                  nodePosition,
                  project.vertices,
                  effectivePoints
                );
                if (!linkPath) return null;
                return linkVertexHandles(
                  selectedLink,
                  linkPath.from,
                  linkPath.to,
                  linkPath.vertices
                ).map((h) => (
                  <VertexHandleMarker
                    key={`${h.kind}-${h.index}`}
                    point={toSvg(h.point)}
                    kind={h.kind}
                    size={markerSize}
                    zoomScale={zoomScale}
                    focused={isHandleFocused(focusedVertex, h)}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      startDrag(
                        h.kind === "vertex"
                          ? { kind: "vertex", target: h.target, index: h.index, point: h.point }
                          : { kind: "insert", target: h.target, index: h.index, point: h.point }
                      );
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (h.kind === "vertex" && canRemoveVertex(h.target, project)) {
                        onDeleteVertex(h.target, h.index);
                      }
                    }}
                  />
                ));
              })()}
            {visibleKinds.subcatchment !== false &&
              selectedSubcatchment &&
              (() => {
                const polygon = effectivePoints(
                  { section: "POLYGONS", name: selectedSubcatchment.name },
                  project.polygons.get(selectedSubcatchment.name) ?? []
                );
                return polygonVertexHandles(selectedSubcatchment.name, polygon).map((h) => (
                  <VertexHandleMarker
                    key={`${h.kind}-${h.index}`}
                    point={toSvg(h.point)}
                    kind={h.kind}
                    size={markerSize}
                    zoomScale={zoomScale}
                    focused={isHandleFocused(focusedVertex, h)}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      startDrag(
                        h.kind === "vertex"
                          ? { kind: "vertex", target: h.target, index: h.index, point: h.point }
                          : { kind: "insert", target: h.target, index: h.index, point: h.point }
                      );
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (h.kind === "vertex" && canRemoveVertex(h.target, project)) {
                        onDeleteVertex(h.target, h.index);
                      }
                    }}
                  />
                ));
              })()}
          </g>
        )}

        {/* Live preview of a subcatchment outline being drawn (App.tsx's `subcatchmentDraft`) — dashed, since it isn't a real element yet. */}
        {drawingPreview.length > 0 && (
          <g className="network-map-drawing-preview">
            {drawingPreview.length > 1 && (
              <polyline points={pointsAttr(drawingPreview)} strokeWidth={4 * zoomScale} />
            )}
            {drawingPreview.map((p, i) => {
              const { x, y } = toSvg(p);
              return <circle key={i} cx={x} cy={y} r={markerSize * 0.6} />;
            })}
          </g>
        )}

        {/* Live preview of an in-progress rectangle/lasso drag-select. */}
        {areaDrag && (
          <polygon
            className={
              areaDrag.shape === "rect"
                ? "network-map-selection-rect"
                : "network-map-selection-lasso"
            }
            points={pointsAttr(areaDragPolygon(areaDrag))}
          />
        )}
      </svg>
      {/* Zoom controls — [+, −, Ajustar a red], matching the exact same
          stack GeoreferencedMap.tsx renders in the same top-left corner
          for the map view, so switching between the two map modes doesn't
          rearrange (or lose) these controls. The wheel-zoom effect above
          is the authoritative reference for the math here — same 1.15
          factor per step (identical feel to one wheel notch), same
          base.width/20 .. base.width*3 clamp — the only difference is
          anchor: a wheel event has a real cursor point to zoom around,
          a button press has none, so this centers on the current viewBox
          middle instead. `.network-map-zoom-controls button:not(:last-child)`
          in networkMap.css already draws the divider lines between them. */}
      {(() => {
        // Aliased into a local const so TS carries the non-null narrowing
        // from the `if (!bounds || !view) return null` early return above
        // into the button `onClick` closures below — a bare `view` inside
        // a nested lambda widens back to `ViewBox | null` for TS even
        // though it is provably non-null here.
        const currentView = view;
        const base = viewFromBounds(bounds);
        const minWidth = base.width / 20;
        const maxWidth = base.width * 3;
        function zoomBy(factor: number) {
          const newWidth = Math.min(maxWidth, Math.max(minWidth, currentView.width * factor));
          if (newWidth === currentView.width) return;
          const ratio = newWidth / currentView.width;
          const centerX = currentView.x + currentView.width / 2;
          const centerY = currentView.y + currentView.height / 2;
          const newHeight = currentView.height * ratio;
          setView({
            x: centerX - newWidth / 2,
            y: centerY - newHeight / 2,
            width: newWidth,
            height: newHeight,
          });
        }
        // 0.5-unit tolerance on the equality check: `viewFromBounds`
        // returns floats and `zoomBy`'s own clamp guards against
        // over-zooming already, but the disabled flag also drives the
        // button's visual state — a tiny float drift is enough to leave
        // a button enabled that would then do nothing.
        const atMin = currentView.width <= minWidth + 0.5;
        const atMax = currentView.width >= maxWidth - 0.5;
        return (
          <div className="network-map-zoom-controls">
            <button
              type="button"
              className="button-icon topbar-icon-button"
              onClick={() => zoomBy(1 / 1.15)}
              disabled={atMin}
              aria-label={t("networkMap.zoomIn")}
              title={t("networkMap.zoomIn")}
            >
              <PlusIcon />
            </button>
            <button
              type="button"
              className="button-icon topbar-icon-button"
              onClick={() => zoomBy(1.15)}
              disabled={atMax}
              aria-label={t("networkMap.zoomOut")}
              title={t("networkMap.zoomOut")}
            >
              <MinusIcon />
            </button>
            <button
              type="button"
              className="button-icon topbar-icon-button"
              onClick={() => setView(viewFromBounds(bounds))}
              aria-label={t("networkMap.zoomToExtent")}
              title={t("networkMap.zoomToExtent")}
            >
              <ZoomExtentIcon />
            </button>
          </div>
        );
      })()}
      {/* Shown only between the base <svg> first painting and layersReady flipping true. */}
      {!layersReady && (
        <div className="map-hint-banner">
          <span>{t("networkMap.loadingNetwork")}</span>
        </div>
      )}
      {showOverview && (
        <svg
          className="network-map-overview"
          viewBox={`${bounds.minX - originX} ${bounds.minY - originY} ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={recenterFromOverview}
          onPointerMove={(e) => {
            if (e.buttons === 1) recenterFromOverview(e);
          }}
        >
          <path
            d={overviewLinksPath}
            className="network-map-overview-links"
            strokeWidth={Math.max(width, height) * 0.004}
          />
          <rect
            className="network-map-overview-viewport"
            x={view.x - originX}
            y={view.y - originY}
            width={view.width}
            height={view.height}
            strokeWidth={Math.max(width, height) * 0.003}
          />
        </svg>
      )}
      <div ref={coordsRef} className="network-map-coords" />
    </div>
  );
}

function VertexHandleMarker({
  point,
  kind,
  size,
  zoomScale,
  focused,
  onPointerDown,
  onDoubleClick,
}: {
  point: Point;
  kind: "vertex" | "midpoint";
  size: number;
  /** Scales the handle's border the same way its own radius (`size`) already scales. */
  zoomScale: number;
  /** Always false for a midpoint, which has no keyboard-focusable identity. */
  focused?: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
}) {
  const baseClass = kind === "vertex" ? "network-map-vertex-handle" : "network-map-midpoint-handle";
  return (
    <circle
      cx={point.x}
      cy={point.y}
      r={kind === "vertex" ? size * 0.6 : size * 0.4}
      className={focused ? `${baseClass} network-map-vertex-focused` : baseClass}
      strokeWidth={(focused ? 3 : kind === "vertex" ? 1.5 : 1) * zoomScale}
      data-draggable="true"
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
    />
  );
}
